import React from "react"
import { observer } from "mobx-react-lite"
import { FieldProps } from "formik"
import without from "lodash/without"
import debounce from "lodash/debounce"

import Dropdown from "@components/prototypes/FilterSidebar/components/Dropdown/Dropdown"
import NotFound from "@components/ui/NotFound/NotFound"
import Loader from "@components/ui/Loader/BarLoader"
import Templates from "@components/ui/Templates"
import { useStore } from "@store/index"
import CheckboxWithLabel from "@components/ui/Checkbox/CheckboxWithLabel"
import ListItem from "@components/ui/ListItem/ListItem"
import DocumentIcon from "@components/ui/Icon/DocumentIcon"
import List from "@components/ui/List/List"
import InfiniteCollection from "@components/modals/EditModal/components/BaseContainer/InfiniteCollection"
import { SearchContext } from "@components/prototypes/SearchContext"
import { Company } from "@framework/types/company"

import styles from "./ManufacturerSelect.module.sass"

const DEBOUNCE_TIMEOUT = 500

export interface CompanySelectProps extends FieldProps<string[]> {
  label?: string
  valueKey?: keyof Company
}

const CompanySelect: React.FC<CompanySelectProps> = observer(
  ({ label, field, form, valueKey = "id" }) => {
    const context = React.useContext(SearchContext)

    const [open, setOpen] = React.useState(false)

    const { value = [], name } = field
    const { setFieldValue } = form

    const { allCompaniesStore } = useStore()

    const handleClick = (newValue: string) => {
      if (!value.includes(newValue)) {
        setFieldValue(name, [...value, newValue])
      } else {
        setFieldValue(name, without(value, newValue))
      }
    }

    const debouncedSearch = React.useCallback(
      debounce(allCompaniesStore.search, DEBOUNCE_TIMEOUT),
      []
    )

    React.useEffect(() => {
      debouncedSearch(context?.query ?? "")
    }, [context?.query])

    const renderRow = (item: Company) => {
      const active = value?.includes(item[valueKey] as string)

      return (
        <ListItem
          active={active}
          onClick={() => handleClick(item[valueKey] as string)}
        >
          <List direction="row" gutter="8" align="center">
            <DocumentIcon icon="factory" size="medium" />
            {item.name}
          </List>
        </ListItem>
      )
    }

    const isChecked = !!value?.length

    const handleUncheck = (e: React.MouseEvent) => {
      if (isChecked) setFieldValue(name, undefined)
      else setOpen(true)
      e.stopPropagation()
    }

    return (
      <Dropdown
        title={label}
        opened={open}
        onClick={() => setOpen((value) => !value)}
        before={
          <CheckboxWithLabel
            checked={!!value?.length}
            onClick={handleUncheck}
          />
        }
      >
        <Templates.RollScript className={styles.root}>
          <div className={styles.container}>
            {allCompaniesStore.state.total === 0 ? (
              allCompaniesStore.state.isLoading ? (
                <Loader size="large" fluid />
              ) : (
                <NotFound>No Manufacture found</NotFound>
              )
            ) : (
              <InfiniteCollection<Company>
                total={allCompaniesStore.state.total}
                getItem={allCompaniesStore.getByIndex}
                renderItem={renderRow}
                minRowHeight={42}
              />
            )}
          </div>
        </Templates.RollScript>
      </Dropdown>
    )
  }
)

export default CompanySelect
